'use client';

import {
    faAlignLeft,
    faArrowAltCircleDown,
    faArrowAltCircleUp,
    faCalendarAlt,
    faCheckSquare,
    faClock,
    faCopy,
    faCreditCard,
    faEuroSign,
    faGripVertical,
    faHashtag,
    faLayerGroup,
    faList,
    faPen,
    faPlus,
    faTable,
    faTags,
    faTimes,
    faTrash,
    faUpload
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Tooltip } from '@ui';
import { StyledCheckbox, StyledInput } from '@ui/Form';
import { cx } from 'class-variance-authority';
import { useClubMemberFields } from 'components/ClubMemberManagement/hooks';
import { TableField } from 'components/FormFields/TableField';
import SelectComponent from 'components/SelectComponent';
import { AnimatePresence, motion } from 'framer-motion';
import { pick } from 'lodash';
import dynamic from 'next/dynamic';
import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import useSWR from 'swr';
import { useClubSlug } from '~/components/Clubs/hooks';
import { CategoryDiscountsEditor } from '~/components/Trainingsjournal/CategoryDiscountsInput';
import { WYSIWYG } from '../forms/WYSIWYG';
import { AutoFillSelect } from './FieldOptions';
import { FormField, PaymentOption } from './FormAssistent.types';
import SectionConfig from './SectionConfig';
import TableEditAssistant from './TableEditAssistant';

const FormItemGroupList = dynamic(() => import('./FormItemGroupList'));

export const DRAG_TYPE = 'QUESTION';
const ROOT_DRAG_SCOPE = 'root';

interface DragItem {
    index: number;
    scope: string;
}

const fieldsContainName = (fields: FormField[] | undefined, names: string[]): boolean =>
    (fields ?? []).some(
        (item) =>
            names.includes(item.name) ||
            (item.type === 'section' && (item.children ?? []).some((child) => fieldsContainName(child.fields, names)))
    );

const LAYOUT_TRANSITION = {
    duration: 0.18,
    ease: [0.25, 0.1, 0.25, 1]
} as const;

const TYPE_LABELS: Record<string, string> = {
    text: 'Textfeld',
    textarea: 'Textbereich',
    address: 'Adresse',
    number: 'Zahlenfeld',
    wysiwyg: 'Textblock',
    checkbox: 'Kästchen',
    select: 'Auswahlfeld',
    date: 'Datum',
    file: 'Datei-Upload',
    iban: 'IBAN',
    table: 'Tabelle',
    payment_options: 'Zahlungsoptionen',
    pricing_table: 'Preistabelle',
    signature: 'Unterschrift',
    legal: 'Rechtliches',
    section: 'Abschnitt'
};

const SWITCHABLE_TYPES = Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }));

const TYPE_ICONS: Record<string, any> = {
    text: faPen,
    textarea: faAlignLeft,
    address: faPen,
    number: faHashtag,
    date: faCalendarAlt,
    file: faUpload,
    select: faList,
    checkbox: faCheckSquare,
    iban: faCreditCard,
    table: faTable,
    wysiwyg: faAlignLeft,
    section: faLayerGroup,
    payment_options: faEuroSign,
    pricing_table: faEuroSign,
    signature: faPen
};

const FILE_TYPE_OPTIONS = [
    { key: 'images', label: 'Bilder', accept: ['image/*'] },
    {
        key: 'documents',
        label: 'Dokumente',
        accept: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv']
    },
    { key: 'video', label: 'Video', accept: ['video/*'] },
    { key: 'audio', label: 'Audio', accept: ['audio/*'] }
] as const;

export interface QuestionBlockProps {
    field: FormField;
    allFields: FormField[];
    index: number;
    isActive: boolean;
    isRestricted: boolean;
    autoFocus?: boolean;
    labelError?: boolean;
    labelErrorFieldNames?: string[];
    onActivate: () => void;
    onLabelChange: (label: string) => void;
    onTypeChange: (type: string) => void;
    onFieldUpdate: (key: keyof FormField, value: any) => void;
    onReplaceField: (updated: FormField) => void;
    onDuplicate: () => void;
    onInsertAbove: () => void;
    onInsertBelow: () => void;
    onDelete: () => void;
    onMoveItem: (dragIndex: number, hoverIndex: number) => void;
    onMoveEnd: () => void;
    onOpenModal: (opts: { title: string; content: React.ReactNode | ((id: string) => React.ReactNode) }) => void;
    onCloseModal: (id: string) => void;
    dragScope?: string;
}

const QuestionBlock: React.FC<QuestionBlockProps> = ({
    field,
    allFields,
    index,
    isActive,
    isRestricted,
    autoFocus,
    labelError = false,
    labelErrorFieldNames = [],
    onActivate,
    onLabelChange,
    onTypeChange,
    onFieldUpdate,
    onReplaceField,
    onDuplicate,
    onInsertAbove,
    onInsertBelow,
    onDelete,
    onMoveItem,
    onMoveEnd,
    onOpenModal,
    onCloseModal,
    dragScope = ROOT_DRAG_SCOPE
}) => {
    const labelRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<HTMLDivElement>(null);
    const overviewRef = useRef<HTMLDivElement>(null);
    const [overviewHeight, setOverviewHeight] = useState(0);

    useEffect(() => {
        const el = overviewRef.current;
        if (!el) return;
        const updateHeight = () => setOverviewHeight(el.scrollHeight);
        const observer = new ResizeObserver(updateHeight);
        updateHeight();
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const { fields: clubFields, isLoading: clubFieldsLoading } = useClubMemberFields();

    const clubSlug = useClubSlug();
    const { data: formItemGroupsData, isLoading: formItemGroupsLoading } = useSWR<{
        data: { id: number; title: string }[];
    }>(field.type === 'pricing_table' ? `club/${clubSlug}/form-item-group` : null);
    const formItemGroups = formItemGroupsData?.data ?? [];

    useEffect(() => {
        if (autoFocus && labelRef.current) {
            labelRef.current.focus();
            labelRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [autoFocus]);

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: DRAG_TYPE,
        item: { index, scope: dragScope },
        end: () => onMoveEnd(),
        collect: (monitor) => ({ isDragging: monitor.isDragging() })
    });

    const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
        accept: DRAG_TYPE,
        collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
        hover(item, monitor) {
            if (item.scope !== dragScope || item.index === index) return;

            const hoverBoundingRect = cardRef.current?.getBoundingClientRect();
            if (!hoverBoundingRect) return;

            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;

            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (item.index < index && hoverClientY < hoverMiddleY) return;
            if (item.index > index && hoverClientY > hoverMiddleY) return;

            onMoveItem(item.index, index);
            item.index = index;
        }
    });

    drag(handleRef);
    drop(dragPreview(cardRef));

    const isRequired = field.rules?.includes('required') ?? false;

    const handleRequiredToggle = () => {
        const rules = [...(field.rules ?? [])];
        const idx = rules.indexOf('required');
        if (idx === -1) rules.push('required');
        else rules.splice(idx, 1);
        onFieldUpdate('rules', rules);
    };

    // ── Inline select option helpers ──────────────────────────────────────────

    const selectOptions = Array.isArray(field.options)
        ? field.options.filter((option): option is string => typeof option === 'string')
        : [];
    const checkboxOptions = Array.isArray(field.options)
        ? field.options.filter((option): option is string => typeof option === 'string')
        : [];
    const paymentOptions = Array.isArray(field.options)
        ? field.options.filter(
              (option): option is PaymentOption =>
                  typeof option === 'object' &&
                  option !== null &&
                  'option' in option &&
                  typeof option.option === 'string' &&
                  'price' in option
          )
        : [];
    const hasCheckboxOptions = field.type === 'checkbox' && checkboxOptions.length > 0;
    const sectionChildren = field.children ?? [];
    const selectedAcceptedFileTypes = new Set(
        (field.acceptedFileTypes ?? '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean)
    );

    const updateAcceptedFileTypes = (optionAccept: readonly string[], checked: boolean) => {
        const nextTypes = new Set(selectedAcceptedFileTypes);

        optionAccept.forEach((value) => {
            if (checked) nextTypes.add(value);
            else nextTypes.delete(value);
        });

        const nextValue = Array.from(nextTypes).join(',');
        onFieldUpdate('acceptedFileTypes', nextValue || undefined);
    };

    const updateSelectOption = (i: number, val: string) =>
        onFieldUpdate(
            'options',
            selectOptions.map((o, idx) => (idx === i ? val : o))
        );

    const removeSelectOption = (i: number) =>
        onFieldUpdate(
            'options',
            selectOptions.filter((_, idx) => idx !== i)
        );

    const addSelectOption = () => onFieldUpdate('options', [...selectOptions, '']);

    const updateCheckboxOption = (i: number, val: string) =>
        onFieldUpdate(
            'options',
            checkboxOptions.map((o, idx) => (idx === i ? val : o))
        );

    const removeCheckboxOption = (i: number) =>
        onFieldUpdate(
            'options',
            checkboxOptions.filter((_, idx) => idx !== i)
        );

    const addCheckboxOption = () => onFieldUpdate('options', [...checkboxOptions, '']);

    const updatePaymentOption = (i: number, key: keyof PaymentOption, val: string | number) =>
        onFieldUpdate(
            'options',
            paymentOptions.map((o, idx) => (idx === i ? { ...o, [key]: val } : o))
        );

    const removePaymentOption = (i: number) =>
        onFieldUpdate(
            'options',
            paymentOptions.filter((_, idx) => idx !== i)
        );

    const addPaymentOption = () => onFieldUpdate('options', [...paymentOptions, { option: '', price: 0 }]);

    // ── Inline configuration (active state) ───────────────────────────────────

    const renderInlineConfig = () => {
        switch (field.type) {
            case 'text':
            case 'textarea':
                return (
                    <div className="tw-space-y-3">
                        <div className="tw-flex tw-gap-2">
                            <StyledInput
                                label="Platzhaltertext"
                                name="placeholder"
                                type="text"
                                value={field.placeholder ?? ''}
                                fullWidth
                                onChange={(e) => onFieldUpdate('placeholder', (e.target as HTMLInputElement).value)}
                            />
                            <StyledInput
                                label="Hilfetext"
                                name="helperText"
                                type="text"
                                value={field.helperText ?? ''}
                                placeholder="Kurze Zusatzinfo unter der Frage"
                                fullWidth
                                onChange={(e) => onFieldUpdate('helperText', (e.target as HTMLInputElement).value)}
                            />
                        </div>
                        <div>
                            <label className="tw-mb-1 tw-block tw-pl-1 tw-text-sm tw-font-medium tw-text-accents-9">
                                Mit Mitgliedsdaten verknüpfen
                            </label>
                            <AutoFillSelect
                                value={typeof field.autofill === 'string' ? field.autofill : undefined}
                                onChange={(val) => onFieldUpdate('autofill', val)}
                                fields={clubFields as any}
                                isLoading={clubFieldsLoading}
                            />
                        </div>
                    </div>
                );

            case 'number':
                return (
                    <div className="tw-flex tw-gap-2">
                        <StyledInput
                            label="Platzhaltertext"
                            name="placeholder"
                            type="text"
                            value={field.placeholder ?? ''}
                            fullWidth
                            onChange={(e) => onFieldUpdate('placeholder', (e.target as HTMLInputElement).value)}
                        />
                        <StyledInput
                            label="Hilfetext"
                            name="helperText"
                            type="text"
                            value={field.helperText ?? ''}
                            placeholder="Kurze Zusatzinfo unter der Frage"
                            fullWidth
                            onChange={(e) => onFieldUpdate('helperText', (e.target as HTMLInputElement).value)}
                        />
                    </div>
                );

            case 'address': {
                // The address field autofills its three parts independently, each from its own source.
                const addressAutofill = (typeof field.autofill === 'object' && field.autofill) || {};
                const updateAddressAutofill = (key: 'street_complete' | 'postalcode' | 'city', val: string) =>
                    onFieldUpdate('autofill', { ...addressAutofill, [key]: val });
                return (
                    <div className="tw-space-y-3">
                        <StyledInput
                            label="Hilfetext"
                            name="helperText"
                            type="text"
                            value={field.helperText ?? ''}
                            placeholder="Kurze Zusatzinfo unter der Frage"
                            fullWidth
                            onChange={(e) => onFieldUpdate('helperText', (e.target as HTMLInputElement).value)}
                        />
                        <div>
                            <label className="tw-mb-1 tw-block tw-pl-1 tw-text-sm tw-font-medium tw-text-accents-9">
                                Straße & Hausnummer verknüpfen mit
                            </label>
                            <AutoFillSelect
                                value={addressAutofill.street_complete}
                                onChange={(val) => updateAddressAutofill('street_complete', val)}
                                fields={clubFields as any}
                                isLoading={clubFieldsLoading}
                                showHint={false}
                            />
                        </div>
                        <div>
                            <label className="tw-mb-1 tw-block tw-pl-1 tw-text-sm tw-font-medium tw-text-accents-9">
                                Postleitzahl verknüpfen mit
                            </label>
                            <AutoFillSelect
                                value={addressAutofill.postalcode}
                                onChange={(val) => updateAddressAutofill('postalcode', val)}
                                fields={clubFields as any}
                                isLoading={clubFieldsLoading}
                                showHint={false}
                            />
                        </div>
                        <div>
                            <label className="tw-mb-1 tw-block tw-pl-1 tw-text-sm tw-font-medium tw-text-accents-9">
                                Stadt verknüpfen mit
                            </label>
                            <AutoFillSelect
                                value={addressAutofill.city}
                                onChange={(val) => updateAddressAutofill('city', val)}
                                fields={clubFields as any}
                                isLoading={clubFieldsLoading}
                            />
                        </div>
                    </div>
                );
            }

            case 'date':
                return (
                    <div className="tw-space-y-3">
                        <StyledInput
                            label="Hilfetext"
                            name="helperText"
                            type="text"
                            value={field.helperText ?? ''}
                            placeholder="Kurze Zusatzinfo unter der Frage"
                            bordered
                            rounded
                            fullWidth
                            onChange={(e) => onFieldUpdate('helperText', (e.target as HTMLInputElement).value)}
                        />
                        <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium">Mit Mitgliedsdaten verknüpfen</label>
                        <AutoFillSelect
                            value={typeof field.autofill === 'string' ? field.autofill : undefined}
                            onChange={(val) => onFieldUpdate('autofill', val)}
                            fields={clubFields as any}
                            isLoading={clubFieldsLoading}
                        />
                        <div className="tw-space-y-4 tw-rounded-xl tw-border tw-border-dashed tw-border-accents-3 tw-p-4">
                            <p className="tw-mb-0 tw-text-sm tw-font-medium">Datumseinschränkungen (optional)</p>
                            {[
                                { key: 'dateMinDaysRelative' as const, label: 'Frühestens ab', defaultDays: 0 },
                                { key: 'dateMaxDaysRelative' as const, label: 'Spätestens bis', defaultDays: 30 }
                            ].map(({ key, label, defaultDays }) => {
                                const days = field[key];
                                const isSet = days !== undefined;
                                const exampleDate = isSet
                                    ? new Date(Date.now() + days * 86400000).toLocaleDateString('de-DE', {
                                          day: '2-digit',
                                          month: 'long',
                                          year: 'numeric'
                                      })
                                    : null;
                                return (
                                    <div key={key} className="tw-flex tw-flex-col tw-gap-1.5">
                                        <label className="tw-block tw-text-xs tw-font-medium tw-text-accents-7">
                                            {label}
                                        </label>
                                        <div
                                            className="tw-flex tw-flex-wrap tw-items-center tw-gap-2"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Button
                                                type="button"
                                                color={!isSet ? 'success' : 'light'}
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onFieldUpdate(key, undefined);
                                                }}
                                            >
                                                Keine
                                            </Button>
                                            <Button
                                                type="button"
                                                color={isSet ? 'success' : 'light'}
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!isSet) onFieldUpdate(key, defaultDays);
                                                }}
                                            >
                                                Einschränkung setzen
                                            </Button>
                                            {isSet && (
                                                <>
                                                    <input
                                                        type="number"
                                                        className="tw-w-20 tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-white tw-px-2 tw-py-1 tw-text-sm focus:tw-border-success focus:tw-outline-none"
                                                        value={days}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) =>
                                                            onFieldUpdate(
                                                                key,
                                                                e.target.value === ''
                                                                    ? undefined
                                                                    : Number(e.target.value)
                                                            )
                                                        }
                                                    />
                                                    <span className="tw-text-xs tw-text-accents-6">
                                                        Tage ab heute
                                                        {exampleDate && (
                                                            <span className="tw-ml-1 tw-font-medium tw-text-accents-8">
                                                                = {exampleDate}
                                                            </span>
                                                        )}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            <div>
                                <label className="tw-mb-2 tw-block tw-text-xs tw-font-medium tw-text-accents-7">
                                    Auswahlbeschränkung
                                </label>
                                <div className="tw-flex tw-flex-wrap tw-gap-2" onClick={(e) => e.stopPropagation()}>
                                    {(
                                        [
                                            { value: undefined, label: 'Beliebiger Tag' },
                                            { value: 'start_of_month', label: 'Nur 1. des Monats' },
                                            { value: 'day_of_month', label: 'Bestimmter Tag im Monat' },
                                            { value: 'start_of_week', label: 'Nur Wochenanfang' }
                                        ] as const
                                    ).map(({ value, label }) => (
                                        <Button
                                            key={label}
                                            type="button"
                                            color={(field.dateSnapTo ?? undefined) === value ? 'success' : 'light'}
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // one update: two in a row lose the first inside sections
                                                onReplaceField({
                                                    ...field,
                                                    dateSnapTo: value,
                                                    dateSnapDay:
                                                        value === 'day_of_month' ? (field.dateSnapDay ?? 15) : undefined
                                                });
                                            }}
                                        >
                                            {label}
                                        </Button>
                                    ))}
                                    {field.dateSnapTo === 'day_of_month' && (
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <span className="tw-text-xs tw-text-accents-6">Nur der</span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={28}
                                                className="tw-w-16 tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-white tw-px-2 tw-py-1 tw-text-sm focus:tw-border-success focus:tw-outline-none"
                                                value={field.dateSnapDay ?? 1}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) =>
                                                    onFieldUpdate(
                                                        'dateSnapDay',
                                                        Math.min(28, Math.max(1, Number(e.target.value) || 1))
                                                    )
                                                }
                                            />
                                            <span className="tw-text-xs tw-text-accents-6">. des Monats (1 bis 28)</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'file':
                return (
                    <div className="tw-space-y-3">
                        <StyledInput
                            label="Hilfetext"
                            name="helperText"
                            type="text"
                            value={field.helperText ?? ''}
                            placeholder="Kurze Zusatzinfo unter dem Upload-Feld"
                            bordered
                            rounded
                            fullWidth
                            onChange={(e) => onFieldUpdate('helperText', (e.target as HTMLInputElement).value)}
                        />
                        <div className="tw-space-y-2">
                            <label className="tw-block tw-text-sm tw-font-medium">Erlaubte Dateitypen</label>
                            <div className="tw-grid tw-gap-2 sm:tw-grid-cols-2" onClick={(e) => e.stopPropagation()}>
                                {FILE_TYPE_OPTIONS.map((option) => (
                                    <StyledCheckbox
                                        key={option.key}
                                        id={`accepted-file-type-${field.name}-${option.key}`}
                                        isSelected={option.accept.every((value) =>
                                            selectedAcceptedFileTypes.has(value)
                                        )}
                                        onChange={(checked) => updateAcceptedFileTypes(option.accept, checked)}
                                        label={option.label}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 'iban':
                return (
                    <div className="tw-space-y-3">
                        <div className="tw-flex tw-gap-2">
                            <StyledInput
                                label="Platzhaltertext"
                                name="placeholder"
                                type="text"
                                value={field.placeholder ?? ''}
                                fullWidth
                                onChange={(e) => onFieldUpdate('placeholder', (e.target as HTMLInputElement).value)}
                            />
                            <StyledInput
                                label="Hilfetext"
                                name="helperText"
                                type="text"
                                value={field.helperText ?? ''}
                                placeholder="Kurze Zusatzinfo unter der Frage"
                                fullWidth
                                onChange={(e) => onFieldUpdate('helperText', (e.target as HTMLInputElement).value)}
                            />
                        </div>
                        <div>
                            <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium">Mit Mitgliedsdaten verknüpfen</label>
                            <AutoFillSelect
                                value={typeof field.autofill === 'string' ? field.autofill : undefined}
                                onChange={(val) => onFieldUpdate('autofill', val)}
                                fields={clubFields as any}
                                isLoading={clubFieldsLoading}
                            />
                        </div>
                    </div>
                );

            case 'checkbox':
                return (
                    <div className="tw-space-y-4">
                        <StyledInput
                            label="Hilfetext"
                            name="helperText"
                            type="text"
                            value={field.helperText ?? ''}
                            placeholder="Kurze Zusatzinfo unter der Frage"
                            bordered
                            rounded
                            fullWidth
                            onChange={(e) => onFieldUpdate('helperText', (e.target as HTMLInputElement).value)}
                        />
                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                            <Button
                                type="button"
                                color={!hasCheckboxOptions ? 'success' : 'light'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onFieldUpdate('options', []);
                                    onFieldUpdate('selectionMode', 'multiple');
                                }}
                            >
                                Einzelnes Kästchen
                            </Button>
                            <Button
                                type="button"
                                color={hasCheckboxOptions && field.selectionMode !== 'single' ? 'success' : 'light'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onFieldUpdate('selectionMode', 'multiple');
                                    if (checkboxOptions.length === 0) onFieldUpdate('options', ['', '']);
                                }}
                            >
                                Mehrfachauswahl
                            </Button>
                            <Button
                                type="button"
                                color={hasCheckboxOptions && field.selectionMode === 'single' ? 'success' : 'light'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onFieldUpdate('selectionMode', 'single');
                                    if (checkboxOptions.length === 0) onFieldUpdate('options', ['', '']);
                                }}
                            >
                                Einzelauswahl
                            </Button>
                        </div>

                        {hasCheckboxOptions ? (
                            <div>
                                <p className="tw-mb-2 tw-text-xs tw-text-accents-6">
                                    {field.selectionMode === 'single'
                                        ? 'In der Vorschau wird nur eine Auswahl gleichzeitig erlaubt.'
                                        : 'Mehrere Optionen können gleichzeitig ausgewählt werden.'}
                                </p>
                                <label className="tw-block tw-text-sm tw-font-medium">Antwortoptionen</label>
                                <ul className="tw-mb-3 tw-space-y-2 tw-pl-0">
                                    {checkboxOptions.map((opt, i) => (
                                        <li key={i} className="tw-flex tw-list-none tw-items-center tw-gap-3">
                                            <StyledInput
                                                type="text"
                                                className="tw-w-full"
                                                size="xl"
                                                value={opt}
                                                placeholder={`Option ${i + 1}`}
                                                onChange={(e) => updateCheckboxOption(i, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <Button
                                                color="error"
                                                size="xs"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeCheckboxOption(i);
                                                }}
                                                title="Entfernen"
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                                <Button
                                    className="tw-ml-auto"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        addCheckboxOption();
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="tw-text-xs" />
                                    Kästchen hinzufügen
                                </Button>
                            </div>
                        ) : (
                            <StyledInput
                                label="Link (optional)"
                                name="link"
                                type="text"
                                placeholder="https://example.de"
                                value={field.link ?? ''}
                                fullWidth
                                onChange={(e) => onFieldUpdate('link', (e.target as HTMLInputElement).value)}
                            />
                        )}
                    </div>
                );

            case 'section':
                return (
                    <SectionConfig
                        field={field}
                        allFields={allFields}
                        index={index}
                        onFieldUpdate={onFieldUpdate}
                        onReplaceField={onReplaceField}
                        onOpenModal={onOpenModal}
                        onCloseModal={onCloseModal}
                        labelErrorFieldNames={labelErrorFieldNames}
                    />
                );

            // ── Inline option list — no modal needed ──────────────────────────
            case 'select':
                return (
                    <div className="tw-space-y-3">
                        <StyledInput
                            label="Hilfetext"
                            name="helperText"
                            type="text"
                            value={field.helperText ?? ''}
                            placeholder="Kurze Zusatzinfo unter der Frage"
                            bordered
                            rounded
                            fullWidth
                            onChange={(e) => onFieldUpdate('helperText', (e.target as HTMLInputElement).value)}
                        />
                        <div onClick={(e) => e.stopPropagation()}>
                            <label className="tw-mb-1 tw-block tw-pl-1 tw-text-sm tw-font-medium tw-text-accents-9">
                                Mit Mitgliedsdaten verknüpfen
                            </label>
                            <AutoFillSelect
                                value={typeof field.autofill === 'string' ? field.autofill : undefined}
                                onChange={(val) => onFieldUpdate('autofill', val)}
                                fields={clubFields as any}
                                isLoading={clubFieldsLoading}
                            />
                        </div>
                        <div>
                            <label className="tw-mb-2 tw-block tw-text-sm tw-font-medium">Optionen</label>
                            <ul className="tw-mb-3 tw-space-y-2 tw-pl-0">
                                {selectOptions.map((opt, i) => (
                                    <li key={i} className="tw-flex tw-list-none tw-items-center tw-gap-3">
                                        <StyledInput
                                            type="text"
                                            className="tw-w-full"
                                            size="xl"
                                            value={opt}
                                            placeholder={`Option ${i + 1}`}
                                            onChange={(e) => updateSelectOption(i, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <Button
                                            color="error"
                                            size="xs"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeSelectOption(i);
                                            }}
                                            title="Entfernen"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                            <Button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    addSelectOption();
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} className="tw-text-xs" />
                                Option hinzufügen
                            </Button>
                        </div>
                    </div>
                );

            case 'payment_options':
                return (
                    <div className="tw-space-y-3">
                        <StyledInput
                            label="Hilfetext"
                            name="helperText"
                            type="text"
                            value={field.helperText ?? ''}
                            placeholder="Kurze Zusatzinfo unter der Frage"
                            bordered
                            rounded
                            fullWidth
                            onChange={(e) => onFieldUpdate('helperText', (e.target as HTMLInputElement).value)}
                        />
                        <div>
                            <div className="tw-mb-2 tw-flex tw-gap-2">
                                <label className="tw-block tw-flex-1 tw-text-sm tw-font-medium">Option</label>
                                <label className="tw-block tw-w-24 tw-text-sm tw-font-medium">Preis (€)</label>
                            </div>
                            <ul className="tw-mb-3 tw-space-y-2 tw-pl-0">
                                {paymentOptions.map((opt, i) => (
                                    <li key={i} className="tw-flex tw-items-center tw-gap-2">
                                        <StyledInput
                                            type="text"
                                            className={`tw-flex-1`}
                                            value={opt.option}
                                            placeholder="z. B. Mitglied"
                                            onChange={(e) => updatePaymentOption(i, 'option', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <StyledInput
                                            type="number"
                                            step={0.01}
                                            min={0}
                                            className={`tw-w-24`}
                                            value={opt.price}
                                            placeholder="9.99"
                                            onChange={(e) => updatePaymentOption(i, 'price', Number(e.target.value))}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <Button
                                            color="error"
                                            size="xs"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removePaymentOption(i);
                                            }}
                                            title="Entfernen"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                            <Button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    addPaymentOption();
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} className="tw-text-xs" />
                                Zahlungsoption hinzufügen
                            </Button>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                            <CategoryDiscountsEditor
                                fixedOnly
                                value={field.category_discounts ?? []}
                                onChange={(next) => onFieldUpdate('category_discounts', next)}
                            />
                        </div>
                    </div>
                );

            case 'pricing_table': {
                const pricingMinRule = (field.rules ?? []).find((r) => typeof r === 'string' && r.startsWith('min:'));
                const pricingMin = pricingMinRule ? Number(pricingMinRule.split(':')[1]) : 0;

                const setPricingMin = (val: number) => {
                    const base = (field.rules ?? []).filter(
                        (r) => typeof r !== 'string' || (!r.startsWith('min:') && r !== 'required')
                    );
                    if (val > 0) {
                        onFieldUpdate('rules', [...base, 'required', `min:${val}`]);
                    } else {
                        onFieldUpdate('rules', base);
                    }
                };

                const isSingle = field.selectionMode === 'single';

                return (
                    <div className="tw-space-y-4">
                        {/* Hint text */}
                        <div>
                            <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium">Hilfetext (optional)</label>
                            <input
                                type="text"
                                className="tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-success focus:tw-outline-none"
                                value={field.helperText ?? ''}
                                placeholder="Kurze Zusatzinfo unter der Frage"
                                onChange={(e) => onFieldUpdate('helperText', e.target.value)}
                            />
                        </div>

                        {/* Preistabelle picker */}
                        <div>
                            <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between">
                                <label className="tw-text-sm tw-font-medium">Preistabelle</label>
                                <Button
                                    size="xs"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenModal({ title: 'Angebote verwalten', content: <FormItemGroupList /> });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTags} className="tw-text-[10px]" />
                                    Angebote verwalten
                                </Button>
                            </div>
                            {formItemGroupsLoading ? (
                                <p className="tw-text-sm tw-text-accents-5">Lade …</p>
                            ) : formItemGroups.length === 0 ? (
                                <div className="tw-space-y-2 tw-rounded-xl tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-1 tw-p-3">
                                    <p className="tw-text-xs tw-font-medium tw-text-accents-8">
                                        So richtest du die Preistabelle ein:
                                    </p>
                                    <ol
                                        className="tw-space-y-1 tw-pl-4 tw-text-xs tw-text-accents-6"
                                        style={{ listStyleType: 'decimal' }}
                                    >
                                        <li>
                                            Klicke auf <strong>Angebote verwalten</strong> oben rechts
                                        </li>
                                        <li>Lege eine Angebotsgruppe an (z.&nbsp;B. Mitgliedsbeiträge)</li>
                                        <li>Füge die einzelnen Angebote mit Preis hinzu</li>
                                        <li>Wähle die Gruppe hier aus</li>
                                    </ol>
                                    <Button
                                        size="xs"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenModal({
                                                title: 'Angebote verwalten',
                                                content: <FormItemGroupList />
                                            });
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTags} className="tw-text-[10px]" />
                                        Angebote verwalten
                                    </Button>
                                </div>
                            ) : (
                                <select
                                    className="tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-success focus:tw-outline-none"
                                    value={field.formItemGroupId ?? ''}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        onFieldUpdate(
                                            'formItemGroupId',
                                            e.target.value ? Number(e.target.value) : undefined
                                        );
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <option value="">— Bitte wählen —</option>
                                    {formItemGroups.map((g) => (
                                        <option key={g.id} value={g.id}>
                                            {g.title}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* Selection mode toggle */}
                        <div>
                            <label className="tw-mb-2 tw-block tw-text-sm tw-font-medium">Auswahlmodus</label>
                            <div className="tw-flex tw-gap-2">
                                <Button
                                    type="button"
                                    color={!isSingle ? 'success' : 'light'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onFieldUpdate('selectionMode', 'multiple');
                                    }}
                                >
                                    Mehrfachauswahl
                                </Button>
                                <Button
                                    type="button"
                                    color={isSingle ? 'success' : 'light'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onFieldUpdate('selectionMode', 'single');
                                    }}
                                >
                                    Einzelauswahl
                                </Button>
                            </div>
                        </div>

                        {/* Minimum required — multiple only */}
                        {!isSingle && (
                            <div>
                                <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium">Pflichtauswahl</label>
                                <div className="tw-flex tw-items-center tw-gap-3" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="number"
                                        min={0}
                                        value={pricingMin}
                                        onChange={(e) => setPricingMin(Number(e.target.value))}
                                        className="tw-w-20 tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-px-2 tw-py-1.5 tw-text-sm focus:tw-border-success focus:tw-outline-none"
                                    />
                                    <span className="tw-text-xs tw-text-accents-6">
                                        {pricingMin === 0
                                            ? 'Alle Positionen sind optional'
                                            : `Mindestens ${pricingMin} Position${pricingMin > 1 ? 'en' : ''} müssen ausgewählt werden`}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="tw-gap-2" onClick={(e) => e.stopPropagation()}>
                            <StyledCheckbox
                                id="showPriceOverview"
                                isSelected={field.showPriceOverview ?? true}
                                onChange={(checked) => onFieldUpdate('showPriceOverview', checked)}
                            >
                                Preisübersicht anzeigen
                            </StyledCheckbox>
                            <p className="tw-mb-0 tw-text-xs tw-text-accents-6">
                                Zeigt dem Nutzer eine Zusammenfassung der ausgewählten Positionen mit Gesamtpreis
                            </p>
                        </div>

                        {/* Column layout */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <StyledCheckbox
                                id="displayLayout"
                                isSelected={field.displayLayout === 'columns'}
                                onChange={(checked) => onFieldUpdate('displayLayout', checked ? 'columns' : 'list')}
                            >
                                Zweispaltige Darstellung
                            </StyledCheckbox>
                            <p className="tw-mb-0 tw-text-xs tw-text-accents-6">
                                Kostenlose Positionen links, kostenpflichtige rechts
                            </p>
                            {field.displayLayout === 'columns' && (
                                <div className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-2">
                                    <div>
                                        <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-accents-7">
                                            Linke Spalte (kostenlos)
                                        </label>
                                        <input
                                            type="text"
                                            className="tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-px-2 tw-py-1.5 tw-text-sm focus:tw-border-success focus:tw-outline-none"
                                            value={field.columnLabelLeft ?? ''}
                                            placeholder="z.B. Inklusive"
                                            onChange={(e) =>
                                                onFieldUpdate('columnLabelLeft', e.target.value || undefined)
                                            }
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-accents-7">
                                            Rechte Spalte (kostenpflichtig)
                                        </label>
                                        <input
                                            type="text"
                                            className="tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-px-2 tw-py-1.5 tw-text-sm focus:tw-border-success focus:tw-outline-none"
                                            value={field.columnLabelRight ?? ''}
                                            placeholder="z.B. Zusatzbeiträge"
                                            onChange={(e) =>
                                                onFieldUpdate('columnLabelRight', e.target.value || undefined)
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }

            case 'wysiwyg':
                return (
                    <div>
                        <WYSIWYG value={field.text} onChange={(val) => onFieldUpdate('text', val)} />
                    </div>
                );

            case 'table':
                return (
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                        <Button
                            color="default"
                            size="sm"
                            onPress={() =>
                                onOpenModal({
                                    title: 'Tabelle konfigurieren',
                                    content: (id) => (
                                        <TableEditAssistant
                                            field={field}
                                            cb={(updated) => {
                                                onCloseModal(id);
                                                onReplaceField(updated);
                                            }}
                                        />
                                    )
                                })
                            }
                        >
                            <FontAwesomeIcon icon={faTable} className="tw-mr-2" />
                            Tabelle konfigurieren
                        </Button>
                        <Button
                            color="light"
                            size="sm"
                            onPress={() =>
                                onOpenModal({
                                    title: 'Gesperrte Zeiten bearbeiten',
                                    content: (
                                        <TableField
                                            field={pick(field, ['rows', 'columns', 'rules'])}
                                            values={field.disabledTimes}
                                            errors={[]}
                                            cb={((val: any) => onFieldUpdate('disabledTimes', val)) as any}
                                            selectIcon={faTimes}
                                        />
                                    )
                                })
                            }
                        >
                            <FontAwesomeIcon icon={faClock} className="tw-mr-2" />
                            Gesperrte Zeiten
                        </Button>
                        {field.inverted && (
                            <p className="tw-mb-0 tw-w-full tw-text-xs tw-text-accents-6">
                                Invertiert: Schüler kreuzen an, wann sie nicht können.
                            </p>
                        )}
                    </div>
                );

            case 'legal':
                return (
                    <p className="tw-text-sm tw-text-accents-6">
                        Rechtliche Felder (AGB, Datenschutz, Widerrufsbelehrung) werden automatisch mit deinen
                        Club-Links verknüpft.
                    </p>
                );

            default:
                return null;
        }
    };

    const isWysiwyg = field.type === 'wysiwyg';
    const supportsRequired =
        !['wysiwyg', 'section'].includes(field.type) &&
        !(field.type === 'pricing_table' && field.selectionMode !== 'single');
    const typeLabel = TYPE_LABELS[field.type] ?? field.type;
    const typeIcon = TYPE_ICONS[field.type] ?? faPen;
    const showRequiredBadge = field.rules?.includes('required');
    const hasNestedLabelError = (field.children ?? []).some((child) =>
        fieldsContainName(child.fields, labelErrorFieldNames)
    );

    return (
        <motion.div
            ref={cardRef}
            layout="position"
            transition={LAYOUT_TRANSITION}
            className={[
                'tw-relative tw-isolate tw-z-0 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-bg-white tw-shadow-sm tw-transition-all tw-duration-150',
                !(field.type === 'section' && isActive) && 'tw-overflow-hidden',
                'tw-ease-out',
                isActive
                    ? 'tw-z-30 tw-border-success tw-shadow-lg tw-ring-1 tw-ring-success/20'
                    : 'tw-cursor-pointer tw-border-accents-3 hover:tw-border-accents-4 hover:tw-shadow-md',
                isDragging ? 'tw-opacity-40' : 'tw-opacity-100',
                isOver ? 'tw-ring-2 tw-ring-success tw-ring-offset-1' : ''
            ].join(' ')}
            onClick={(e) => {
                e.stopPropagation();
                if (!isActive) onActivate();
            }}
        >
            <div
                ref={handleRef}
                className="tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-z-10 tw-flex tw-w-14 tw-cursor-grab tw-items-start tw-justify-center tw-py-2 active:tw-cursor-grabbing"
                onClick={(e) => e.stopPropagation()}
                title="Verschieben"
            >
                {/* Animated active-state background overlay */}
                <motion.div
                    className="tw-pointer-events-none tw-absolute tw-inset-0 tw-border-0 tw-border-r tw-border-solid tw-border-accents-2 tw-bg-accents-0/80"
                    animate={{ opacity: isActive ? 1 : 0 }}
                    transition={LAYOUT_TRANSITION}
                />
                <div className="tw-relative tw-flex tw-h-10 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-transition-colors hover:tw-bg-accents-1">
                    <FontAwesomeIcon icon={faGripVertical} className="tw-text-sm tw-text-accents-4" />
                </div>
            </div>

            <div className={cx(isActive ? 'tw-pl-[72px]' : 'tw-pl-14', 'tw-py-4 tw-pr-4')}>
                {/* Wysiwyg header */}
                {isWysiwyg && (
                    <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-3">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <span className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-accents-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-accents-7">
                                <FontAwesomeIcon icon={typeIcon} className="tw-text-[10px]" />
                                Textblock
                            </span>
                            {showRequiredBadge && (
                                <span className="tw-rounded-full tw-bg-success/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-success">
                                    Pflichtfeld
                                </span>
                            )}
                        </div>
                        {isActive && !isRestricted ? (
                            <div className="tw-w-[220px] tw-shrink-0" onClick={(e) => e.stopPropagation()}>
                                <SelectComponent
                                    value={field.type}
                                    onChange={(value) => onTypeChange(String(value))}
                                    options={SWITCHABLE_TYPES}
                                    buttonProps={{
                                        containerClassName: 'tw-my-0',
                                        className: '!tw-justify-between !tw-rounded-xl'
                                    }}
                                />
                            </div>
                        ) : (
                            <span className="tw-shrink-0 tw-self-center tw-whitespace-nowrap tw-rounded-full tw-bg-accents-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-accents-6">
                                {typeLabel}
                            </span>
                        )}
                    </div>
                )}
                {!isWysiwyg && (
                    <>
                        <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                <span className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-accents-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-accents-7">
                                    <FontAwesomeIcon icon={typeIcon} className="tw-text-[10px]" />
                                    {typeLabel}
                                </span>
                                {supportsRequired && showRequiredBadge && (
                                    <span className="tw-rounded-full tw-bg-success/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-success">
                                        Pflichtfeld
                                    </span>
                                )}
                                {(labelError || hasNestedLabelError) && (
                                    <span className="tw-rounded-full tw-bg-error/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-error">
                                        {labelError ? 'Titel fehlt' : 'Feldtitel fehlt'}
                                    </span>
                                )}
                            </div>
                            {!isActive && field.type === 'section' && sectionChildren.length > 0 && (
                                <span className="tw-text-xs tw-font-medium tw-text-accents-5">
                                    {sectionChildren.length} {sectionChildren.length === 1 ? 'Schritt' : 'Schritte'}
                                </span>
                            )}
                        </div>
                        <div className="tw-flex tw-items-start tw-gap-4">
                            <div className="tw-flex-1">
                                <StyledInput
                                    ref={labelRef as React.Ref<HTMLInputElement>}
                                    name={`field-label-${field.name}`}
                                    type="text"
                                    flush
                                    fullWidth
                                    containerClassName="!tw-my-0"
                                    className="tw-text-base tw-font-semibold tw-text-accents-9 placeholder:tw-font-medium placeholder:tw-text-accents-5"
                                    value={field.label}
                                    placeholder="Feldtitel eingeben"
                                    required={field.type !== 'section'}
                                    isInvalid={labelError}
                                    helperText={labelError ? 'Feldtitel ist erforderlich.' : undefined}
                                    onChange={(e) => onLabelChange(e.target.value)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isActive) onActivate();
                                    }}
                                />
                                {field.helperText && (
                                    <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-accents-6">{field.helperText}</p>
                                )}
                                {field.type === 'section' &&
                                    sectionChildren.length > 0 &&
                                    (() => {
                                        const showOverview = !isActive;
                                        return (
                                            <motion.div
                                                initial={false}
                                                animate={{
                                                    height: showOverview ? overviewHeight : 0,
                                                    opacity: showOverview ? 1 : 0
                                                }}
                                                transition={LAYOUT_TRANSITION}
                                                className="tw-overflow-hidden"
                                                style={{ pointerEvents: showOverview ? 'auto' : 'none' }}
                                            >
                                                <div ref={overviewRef} className="tw-space-y-2 tw-pt-3">
                                                    {sectionChildren.map((child, i) => (
                                                        <div
                                                            key={child.id}
                                                            className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-accents-3"
                                                        >
                                                            <div className="tw-flex tw-items-center tw-gap-2.5 tw-bg-accents-1 tw-px-3 tw-py-1.5">
                                                                <span className="tw-flex tw-size-4 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-accents-5 tw-text-[9px] tw-font-bold tw-text-white">
                                                                    {i + 1}
                                                                </span>
                                                                <span className="tw-flex-1 tw-truncate tw-text-xs tw-font-semibold tw-text-accents-8">
                                                                    {child.label || `Schritt ${i + 1}`}
                                                                </span>
                                                            </div>
                                                            {(child.fields?.length ?? 0) > 0 && (
                                                                <>
                                                                    {child.fields!.map((f) => (
                                                                        <div
                                                                            key={f.name}
                                                                            className="tw-flex tw-items-center tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-accents-2 tw-px-3 tw-py-1.5"
                                                                        >
                                                                            <div className="tw-flex tw-size-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-bg-accents-2 tw-text-accents-6">
                                                                                <FontAwesomeIcon
                                                                                    icon={TYPE_ICONS[f.type] ?? faPen}
                                                                                    className="tw-text-[9px]"
                                                                                />
                                                                            </div>
                                                                            {(f.type === 'wysiwyg' || f.label) && (
                                                                                <span className="tw-flex-1 tw-truncate tw-text-xs tw-text-accents-9">
                                                                                    {f.type === 'wysiwyg'
                                                                                        ? 'Textblock'
                                                                                        : f.label}
                                                                                </span>
                                                                            )}
                                                                            <span className="tw-shrink-0 tw-text-[10px] tw-text-accents-7">
                                                                                {TYPE_LABELS[f.type] ?? f.type}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        );
                                    })()}
                            </div>

                            <div className="tw-flex tw-shrink-0 tw-justify-end">
                                {isActive && !isRestricted && (
                                    <div className="tw-w-[220px]" onClick={(e) => e.stopPropagation()}>
                                        <SelectComponent
                                            value={field.type}
                                            onChange={(value) => onTypeChange(String(value))}
                                            options={SWITCHABLE_TYPES}
                                            buttonProps={{
                                                containerClassName: 'tw-my-0',
                                                className: '!tw-justify-between !tw-rounded-xl'
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
                {isActive && (
                    <div className={cx('tw-pt-4', field.type !== 'section' && 'tw-overflow-hidden')}>
                        {renderInlineConfig()}
                    </div>
                )}
                <AnimatePresence initial={false} mode="sync">
                    {isActive && (
                        <motion.div
                            key="footer"
                            layout="position"
                            transition={LAYOUT_TRANSITION}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, transition: { duration: 0.08 } }}
                            className="tw-mt-4"
                        >
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-accents-0 tw-p-3">
                                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                    {!isRestricted && (
                                        <Tooltip flat withArrow placement="left" tooltip="Element oben hinzufügen">
                                            <Button
                                                color="light"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onInsertAbove();
                                                }}
                                                aria-label="Element oben hinzufügen"
                                            >
                                                <FontAwesomeIcon icon={faArrowAltCircleUp} />
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {!isRestricted && (
                                        <Tooltip flat withArrow placement="left" tooltip="Element unten hinzufügen">
                                            <Button
                                                color="light"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onInsertBelow();
                                                }}
                                                size="sm"
                                                aria-label="Element unten hinzufügen"
                                            >
                                                <FontAwesomeIcon icon={faArrowAltCircleDown} />
                                            </Button>
                                        </Tooltip>
                                    )}

                                    <div className="tw-hidden tw-h-8 tw-w-px tw-bg-accents-2 sm:tw-block" />
                                    {!isRestricted && (
                                        <Tooltip flat withArrow placement="left" tooltip="Duplizieren">
                                            <Button
                                                color="light"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDuplicate();
                                                }}
                                                aria-label="Duplizieren"
                                            >
                                                <FontAwesomeIcon icon={faCopy} />
                                            </Button>
                                        </Tooltip>
                                    )}

                                    <Tooltip flat withArrow placement="left" tooltip="Löschen">
                                        <Button
                                            color="error"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete();
                                            }}
                                            aria-label="Löschen"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </Tooltip>
                                </div>

                                {supportsRequired && (
                                    <div
                                        className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-bg-white tw-px-3 tw-py-2"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRequiredToggle();
                                        }}
                                    >
                                        <span className="tw-select-none tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-accents-6">
                                            Pflichtfeld
                                        </span>
                                        <div
                                            className={`tw-relative tw-inline-flex tw-h-5 tw-w-10 tw-rounded-full tw-transition-colors tw-duration-200 ${isRequired ? 'tw-bg-success' : 'tw-bg-accents-3'}`}
                                        >
                                            <span
                                                className={`tw-absolute tw-left-0.5 tw-top-0.5 tw-size-4 tw-rounded-full tw-bg-white tw-shadow tw-transition-transform tw-duration-200 ${isRequired ? 'tw-translate-x-5' : 'tw-translate-x-0'}`}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

export default QuestionBlock;
